import { type ComponentPropsWithoutRef, type Ref } from 'react';
import classNames from 'classnames';
import FormError from '@/components/FormError';
import IcMagnifyingGlass from '@/assets/icons/MagnifyingGlass.svg?react';
import IcCross from '@/assets/icons/Cross.svg?react';
import styles from './FormInput.module.scss';

type FormInputProps = Omit<ComponentPropsWithoutRef<'input'>, 'className'> & {
  before?: 'search';
  className?: {
    label?: string;
    input?: string;
    inputWrapper?: string;
  };
  condensed?: boolean;
  errorMessage?: string;
  helperText?: string;
  id: string;
  label: string;
  onClear?: () => void;
  optional?: boolean;
  optionalLabel?: string;
  ref?: Ref<HTMLInputElement>;
};

const FormInput = ({
  before,
  className,
  condensed,
  autoComplete = 'off',
  errorMessage,
  helperText,
  id,
  label,
  onClear,
  optional,
  optionalLabel,
  type = 'text',
  ref,
  ...rest
}: FormInputProps) => {
  return (
    <>
      <label
        htmlFor={id}
        className={classNames(styles.label, className?.label, {
          [styles.condensed]: condensed,
        })}
      >
        {label}
        {optional && (
          <span className={styles.optionalTag}>
            {optionalLabel ?? 'Optional'}
          </span>
        )}
      </label>
      <div
        className={classNames(styles.inputWrapper, className?.inputWrapper, {
          [styles.hasBefore]: !!before,
          [styles.hasAfter]: !!onClear,
          [styles.hasError]: !!errorMessage,
          [styles.hasHelper]: !!helperText,
          [styles.condensed]: condensed,
        })}
      >
        {before && before === 'search' && (
          <IcMagnifyingGlass className={styles.before} />
        )}
        <input
          {...rest}
          ref={ref}
          autoComplete={autoComplete}
          className={classNames(styles.input, className?.input)}
          type={type}
          id={id}
        />
        {onClear && (
          <button type="button" onClick={onClear} className={styles.after}>
            <IcCross className={styles.clearIcon} />
          </button>
        )}
      </div>
      {helperText && (
        <div
          className={classNames(styles.helper, {
            [styles.hasError]: !!errorMessage,
          })}
        >
          {helperText}
        </div>
      )}
      {errorMessage && !condensed && <FormError message={errorMessage} />}
    </>
  );
};

export default FormInput;
